import React, { useState, useEffect, useCallback } from "react";
import Grid from "@mui/material/Grid";
import TextField from "../../../Components/common/TextField";
import SearchField from "../../../Components/common/SearchField";
import Button from "../../../Components/common/Button";
import ConfirmDialog from "../../../Components/common/ConfirmDialoag";
import Dialog from "../../../Components/common/Dialog";
import { connect } from "react-redux";
import { checkPrivileges, errorHandler } from "../../../Helpers";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SendIcon from "@mui/icons-material/Send";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import CloseIcon from "@mui/icons-material/Close";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CancelIcon from "@mui/icons-material/Cancel";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import axios from "axios";
import instance from "../../../axios";
import MenuItem from "../../../Components/common/MenuItem";
import Tooltip from "../../../Components/common/Tooltip";
import withTranslationWrapper from "../../../HOC/withTranslation";
import { notifySuccess, notifyError } from "../../../Utils/CustomNotifcations";
import { addCommands, removedCommands, getCommands } from "../../../Actions/Commands/index";
import { timeZonesTypes } from "../../../Components/Attributes/timezone";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment-timezone";

// System command types shown for every device (fallback when types API unavailable)
const SYSTEM_COMMAND_TYPES = [
  "custom",
  "positionPeriodic",
  "engineStop",
  "engineResume",
  "alarmArm",
  "alarmDisarm",
  "setTimezone",
  "sendSms",
  "rebootDevice",
];

const TYPES_WITH_DATA = new Set([
  "custom",
  "silenceTime",
  "setPhonebook",
  "voiceMessage",
  "outputControl",
  "setIndicator",
  "configuration",
  "setOdometer",
  "alarmClock",
  "alarmSpeed",
  "alarmVibration",
  "setSilenceTime",
]);
const TYPES_WITH_ENABLE = new Set([
  "alarmFall","alarmRemove","alarmBattery","modeDeepSleep","modePowerSaving",
  "setAgps","voiceMonitoring","modifyPowerSaving","modifyDeepSleep","alarmSos",
]);
const TYPES_WITH_PHONE = new Set(["sendSms","sendUssd","sosNumber"]);
const TYPES_WITH_MESSAGE = new Set(["sendSms","message"]);
const TYPES_WITH_INDEX = new Set(["sosNumber","outputControl"]);
const FREQUENCY_TYPES = [{ type: "s" }, { type: "m" }, { type: "h" }];

function commandTypeNeedsInput(type) {
  if (!type) return false;
  return (
    TYPES_WITH_DATA.has(type) || TYPES_WITH_ENABLE.has(type) ||
    TYPES_WITH_PHONE.has(type) || TYPES_WITH_MESSAGE.has(type) ||
    TYPES_WITH_INDEX.has(type) || type === "positionPeriodic" ||
    type === "setTimezone" || type === "setConnection" || type === "novementAlarm"
  );
}

function buildCommandAttributes(formData) {
  const attrs = {};
  const type = formData.type || "";
  if (TYPES_WITH_DATA.has(type) && formData.data) attrs.data = formData.data;
  if (type === "positionPeriodic") {
    if (formData.frequency) attrs.frequency = formData.frequency;
    if (formData.frequencyType) attrs.frequencyType = formData.frequencyType;
  }
  if (type === "setTimezone" && formData.timezone) attrs.timezone = formData.timezone;
  if (TYPES_WITH_PHONE.has(type) && formData.phoneNumber) attrs.phoneNumber = formData.phoneNumber;
  if (TYPES_WITH_MESSAGE.has(type) && formData.messages) attrs.messages = formData.messages;
  if (TYPES_WITH_INDEX.has(type) && formData.index) attrs.index = formData.index;
  if (type === "setConnection") {
    if (formData.server) attrs.server = formData.server;
    if (formData.port) attrs.port = formData.port;
  }
  if (type === "novementAlarm" && formData.radius) attrs.radius = formData.radius;
  if (TYPES_WITH_ENABLE.has(type)) attrs.enable = formData.enable || false;
  return attrs;
}

const emptyCreateData = {
  deviceId: 0, id: -1, textChannel: false, type: "custom", description: "", attributes: { data: "" },
};

const emptyScheduleForm = {
  scheduledTime: "",
  description: "",
  data: "", frequency: "", frequencyType: "s", timezone: "",
  phoneNumber: "", messages: "", index: "", server: "", port: "", radius: "", enable: false,
};

// ── Inline send fields ──────────────────────────────────────────────────────
function InlineSendFields({ type, initialAttributes, isSending, onSend, onCancel, translate }) {
  const attrs = initialAttributes || {};
  const [fields, setFields] = useState({
    data: attrs.data || "",
    frequency: attrs.frequency || "",
    frequencyType: attrs.frequencyType || "s",
    timezone: attrs.timezone || "",
    phoneNumber: attrs.phoneNumber || "",
    messages: attrs.messages || "",
    index: attrs.index || "",
    server: attrs.server || "",
    port: attrs.port || "",
    radius: attrs.radius || "",
    enable: Boolean(attrs.enable),
  });

  const handleChange = (field, value) => setFields((prev) => ({ ...prev, [field]: value }));

  const handleSend = () => {
    const built = buildCommandAttributes({ ...fields, type });
    onSend(built);
  };

  return (
    <div style={{ padding: "8px 0 4px" }}>
      <Grid container spacing={1}>
        {TYPES_WITH_DATA.has(type) && (
          <Grid item xs={12}>
            <TextField margin="dense" label={translate("commandData")} variant="outlined" fullWidth
              value={fields.data} onChange={(e) => handleChange("data", e.target.value)} />
          </Grid>
        )}
        {type === "positionPeriodic" && (
          <>
            <Grid item xs={6}>
              <TextField type="number" margin="dense" label={translate("commandFrequency")} variant="outlined"
                fullWidth value={fields.frequency} onChange={(e) => handleChange("frequency", e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <TextField select margin="dense" label={translate("commandFrequencyType")} variant="outlined"
                fullWidth value={fields.frequencyType} onChange={(e) => handleChange("frequencyType", e.target.value)}>
                {FREQUENCY_TYPES.map((ft) => <MenuItem key={ft.type} value={ft.type}>{ft.type}</MenuItem>)}
              </TextField>
            </Grid>
          </>
        )}
        {type === "setTimezone" && (
          <Grid item xs={12}>
            <TextField select margin="dense" label={translate("commandTimezone")} variant="outlined"
              fullWidth value={fields.timezone} onChange={(e) => handleChange("timezone", e.target.value)}>
              {timeZonesTypes.map((tz) => <MenuItem key={tz.key} value={tz.key}>{tz.label}</MenuItem>)}
            </TextField>
          </Grid>
        )}
        {TYPES_WITH_INDEX.has(type) && (
          <Grid item xs={6}>
            <TextField type="number" margin="dense" label={translate("commandIndex")} variant="outlined"
              fullWidth value={fields.index} onChange={(e) => handleChange("index", e.target.value)} />
          </Grid>
        )}
        {TYPES_WITH_PHONE.has(type) && (
          <Grid item xs={6}>
            <TextField margin="dense" label={translate("commandPhone")} variant="outlined"
              fullWidth value={fields.phoneNumber} onChange={(e) => handleChange("phoneNumber", e.target.value)} />
          </Grid>
        )}
        {TYPES_WITH_MESSAGE.has(type) && (
          <Grid item xs={12}>
            <TextField margin="dense" label={translate("Messages")} variant="outlined"
              fullWidth value={fields.messages} onChange={(e) => handleChange("messages", e.target.value)} />
          </Grid>
        )}
        {type === "setConnection" && (
          <>
            <Grid item xs={6}>
              <TextField margin="dense" label={translate("commandServer")} variant="outlined"
                fullWidth value={fields.server} onChange={(e) => handleChange("server", e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <TextField type="number" margin="dense" label={translate("commandPort")} variant="outlined"
                fullWidth value={fields.port} onChange={(e) => handleChange("port", e.target.value)} />
            </Grid>
          </>
        )}
        {type === "novementAlarm" && (
          <Grid item xs={12}>
            <TextField type="number" margin="dense" label={translate("commandRadius")} variant="outlined"
              fullWidth value={fields.radius} onChange={(e) => handleChange("radius", e.target.value)} />
          </Grid>
        )}
        {TYPES_WITH_ENABLE.has(type) && (
          <Grid item xs={12} style={{ display: "flex", alignItems: "center", paddingLeft: 4 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13 }}>
              <input type="checkbox" checked={fields.enable} onChange={(e) => handleChange("enable", e.target.checked)} />
              {translate("commandEnable")}
            </label>
          </Grid>
        )}
      </Grid>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 6 }}>
        <Button size="small" className="button-white" onClick={onCancel} disabled={isSending}>
          {translate("sharedCancel")}
        </Button>
        <Button size="small" onClick={handleSend} disabled={isSending}>
          {isSending ? translate("loading") : translate("commandSend")}
        </Button>
      </div>
    </div>
  );
}

// ── Schedule form fields (shared by create/edit) ───────────────────────────
function ScheduleFormFields({ form, onChange, translate }) {
  const type = form.type || "custom";
  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DateTimePicker
            label={translate("scheduledTime")}
            value={form.scheduledTime ? moment(form.scheduledTime) : null}
            onChange={(val) => onChange("scheduledTime", val ? val.toISOString() : "")}
            minDateTime={moment()}
            slotProps={{
              textField: {
                margin: "dense", variant: "outlined", fullWidth: true, size: "small",
                style: { marginTop: 8 },
              },
            }}
          />
        </LocalizationProvider>
      </Grid>
      <Grid item xs={12}>
        <TextField margin="dense" label={translate("sharedDescription")} variant="outlined" fullWidth
          value={form.description || ""} onChange={(e) => onChange("description", e.target.value)} />
      </Grid>
      {TYPES_WITH_DATA.has(type) && (
        <Grid item xs={12}>
          <TextField margin="dense" label={translate("commandData")} variant="outlined" fullWidth
            value={form.data || ""} onChange={(e) => onChange("data", e.target.value)} />
        </Grid>
      )}
      {type === "positionPeriodic" && (
        <>
          <Grid item xs={6}>
            <TextField type="number" margin="dense" label={translate("commandFrequency")} variant="outlined"
              fullWidth value={form.frequency || ""} onChange={(e) => onChange("frequency", e.target.value)} />
          </Grid>
          <Grid item xs={6}>
            <TextField select margin="dense" label={translate("commandFrequencyType")} variant="outlined"
              fullWidth value={form.frequencyType || "s"} onChange={(e) => onChange("frequencyType", e.target.value)}>
              {FREQUENCY_TYPES.map((ft) => <MenuItem key={ft.type} value={ft.type}>{ft.type}</MenuItem>)}
            </TextField>
          </Grid>
        </>
      )}
      {type === "setTimezone" && (
        <Grid item xs={12}>
          <TextField select margin="dense" label={translate("commandTimezone")} variant="outlined"
            fullWidth value={form.timezone || ""} onChange={(e) => onChange("timezone", e.target.value)}>
            {timeZonesTypes.map((tz) => <MenuItem key={tz.key} value={tz.key}>{tz.label}</MenuItem>)}
          </TextField>
        </Grid>
      )}
      {TYPES_WITH_INDEX.has(type) && (
        <Grid item xs={6}>
          <TextField type="number" margin="dense" label={translate("commandIndex")} variant="outlined"
            fullWidth value={form.index || ""} onChange={(e) => onChange("index", e.target.value)} />
        </Grid>
      )}
      {TYPES_WITH_PHONE.has(type) && (
        <Grid item xs={6}>
          <TextField margin="dense" label={translate("commandPhone")} variant="outlined"
            fullWidth value={form.phoneNumber || ""} onChange={(e) => onChange("phoneNumber", e.target.value)} />
        </Grid>
      )}
      {TYPES_WITH_MESSAGE.has(type) && (
        <Grid item xs={12}>
          <TextField margin="dense" label={translate("Messages")} variant="outlined"
            fullWidth value={form.messages || ""} onChange={(e) => onChange("messages", e.target.value)} />
        </Grid>
      )}
      {type === "setConnection" && (
        <>
          <Grid item xs={6}>
            <TextField margin="dense" label={translate("commandServer")} variant="outlined"
              fullWidth value={form.server || ""} onChange={(e) => onChange("server", e.target.value)} />
          </Grid>
          <Grid item xs={6}>
            <TextField type="number" margin="dense" label={translate("commandPort")} variant="outlined"
              fullWidth value={form.port || ""} onChange={(e) => onChange("port", e.target.value)} />
          </Grid>
        </>
      )}
      {type === "novementAlarm" && (
        <Grid item xs={12}>
          <TextField type="number" margin="dense" label={translate("commandRadius")} variant="outlined"
            fullWidth value={form.radius || ""} onChange={(e) => onChange("radius", e.target.value)} />
        </Grid>
      )}
      {TYPES_WITH_ENABLE.has(type) && (
        <Grid item xs={12} style={{ display: "flex", alignItems: "center", paddingLeft: 4 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13 }}>
            <input type="checkbox" checked={Boolean(form.enable)}
              onChange={(e) => onChange("enable", e.target.checked)} />
            {translate("commandEnable")}
          </label>
        </Grid>
      )}
    </Grid>
  );
}

// ── Single command row ─────────────────────────────────────────────────────
function CommandRow({
  item, mode, canSend, canSchedule, canUnassign, canAssign, canEdit, canDelete,
  onAssign, onUnassign, onEdit, onDelete, onSchedule, onSend, translate, themecolors,
  isAssigning, isUnassigning,
}) {
  const [expanded, setExpanded] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const needsInput = commandTypeNeedsInput(item.type);
  const isSystem = item.kind === "system";

  const toggleSend = () => setExpanded((v) => !v);

  const handleConfirmSend = async () => {
    setIsSending(true);
    try {
      const attrs = item.kind === "custom" ? { ...(item.attributes || {}) } : {};
      await onSend(item, attrs);
      setExpanded(false);
    } finally {
      setIsSending(false);
    }
  };

  const handleFieldsSend = async (attrs) => {
    setIsSending(true);
    try {
      await onSend(item, attrs);
      setExpanded(false);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div style={{
      background: `${themecolors.themeLightColor}22`,
      borderRadius: 4, marginBottom: 6, overflow: "hidden",
      border: `1px solid ${themecolors.themeLightColor}44`,
    }}>
      <div style={{ display: "flex", alignItems: "center", padding: "6px 10px", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, fontWeight: 500 }}>{item.name}</span>
            {mode === "available" && (
              <span style={{
                fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em",
                padding: "1px 6px", borderRadius: 10,
                background: isSystem ? "transparent" : themecolors.menuActiveBackground,
                color: isSystem ? themecolors.textColor : "#fff",
                border: isSystem ? `1px solid ${themecolors.themeLightColor}` : "none",
              }}>
                {isSystem ? translate("systemCommand") : translate("customCommand")}
              </span>
            )}
          </div>
          {item.description ? (
            <div style={{ fontSize: 11, color: themecolors.textColor, opacity: 0.65, marginTop: 1 }}>
              {item.description}
            </div>
          ) : null}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
          {mode === "assignable" && (
            <>
              {canAssign && (
                <Button size="small" disabled={isAssigning}
                  onClick={() => onAssign && onAssign(item)}
                  style={{ fontSize: 11, padding: "2px 8px", height: 26 }}>
                  <LinkIcon style={{ fontSize: 13, marginRight: 2 }} />
                  {translate("assign")}
                </Button>
              )}
              {canEdit && (
                <Tooltip title={translate("edit")}>
                  <EditIcon className="actionButton" fontSize="small"
                    style={{ margin: "0 3px", cursor: "pointer" }}
                    onClick={() => onEdit && onEdit(item)} />
                </Tooltip>
              )}
              {canDelete && (
                <Tooltip title={translate("delete")}>
                  <DeleteIcon className="actionButton" fontSize="small"
                    style={{ margin: "0 3px", cursor: "pointer" }}
                    onClick={() => onDelete && onDelete(item)} />
                </Tooltip>
              )}
            </>
          )}
          {mode === "available" && (
            <>
              {!isSystem && canUnassign && (
                <Button size="small" className="button-white" disabled={isUnassigning}
                  onClick={() => onUnassign && onUnassign(item)}
                  style={{ fontSize: 11, padding: "2px 8px", height: 26 }}>
                  <LinkOffIcon style={{ fontSize: 13, marginRight: 2 }} />
                  {translate("unassign")}
                </Button>
              )}
              {canSchedule && (
                <Button size="small" className="button-white"
                  onClick={() => onSchedule && onSchedule(item)}
                  style={{ fontSize: 11, padding: "2px 8px", height: 26 }}>
                  <CalendarTodayIcon style={{ fontSize: 13, marginRight: 2 }} />
                  {translate("schedule")}
                </Button>
              )}
              {canSend && (
                <Button size="small" onClick={toggleSend}
                  style={{
                    fontSize: 11, padding: "2px 8px", height: 26,
                    background: expanded ? "#999" : themecolors.menuActiveBackground, color: "#fff",
                  }}>
                  {expanded ? (
                    <><CloseIcon style={{ fontSize: 13, marginRight: 2 }} />{translate("sharedCancel")}</>
                  ) : (
                    <><SendIcon style={{ fontSize: 13, marginRight: 2 }} />{translate("commandSend")}</>
                  )}
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {expanded && canSend && (
        <div style={{ borderTop: `1px solid ${themecolors.themeLightColor}44`, background: `${themecolors.themeLightColor}11`, padding: "4px 10px 10px" }}>
          {needsInput ? (
            <InlineSendFields type={item.type}
              initialAttributes={item.kind === "custom" ? item.attributes : {}}
              isSending={isSending} onSend={handleFieldsSend}
              onCancel={toggleSend} translate={translate} themecolors={themecolors} />
          ) : (
            <div style={{ paddingTop: 6 }}>
              <p style={{ fontSize: 12, marginBottom: 8, color: themecolors.textColor }}>
                {translate("areYouSureToSendThisCommand")} <strong>{item.name}</strong>?
              </p>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <Button size="small" className="button-white" onClick={toggleSend} disabled={isSending}>
                  {translate("sharedCancel")}
                </Button>
                <Button size="small" disabled={isSending} onClick={handleConfirmSend}>
                  {isSending ? translate("loading") : translate("commandSend")}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Scheduled command row ──────────────────────────────────────────────────
function ScheduledRow({ command, deviceName, canUpdate, isCancelling, onEdit, onCancel, translate, themecolors }) {
  const typeLabel = translate(`commands.${command.type}`) || command.type;
  const name = command.description || typeLabel;
  const when = (() => {
    try {
      return moment(command.scheduledTime).format("MMM D, YYYY HH:mm");
    } catch {
      return command.scheduledTime;
    }
  })();
  // subtitle: typeLabel · deviceName (matches fms_v03 metaParts)
  const metaParts = [typeLabel, deviceName].filter(Boolean);

  return (
    <div style={{
      background: `${themecolors.themeLightColor}22`, borderRadius: 4, marginBottom: 6,
      border: `1px solid ${themecolors.themeLightColor}44`,
    }}>
      <div style={{ display: "flex", alignItems: "center", padding: "6px 10px", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: themecolors?.textColor }}>
            {name}
          </div>
          {metaParts.length > 0 && (
            <div style={{ fontSize: 11, color: themecolors?.textColor, opacity: 0.65, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {metaParts.join(" · ")}
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          <span style={{ fontSize: 11, color: themecolors?.textColor, opacity: 0.7, display: "flex", alignItems: "center", gap: 3 }}>
            <ScheduleIcon style={{ fontSize: 13 }} />
            {when}
          </span>
          <span style={{
            fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 10,
            background: themecolors.menuActiveBackground, color: "#fff",
          }}>
            {translate("scheduled")}
          </span>
          {canUpdate && (
            <>
              <Tooltip title={translate("edit")}>
                <EditIcon className="actionButton" fontSize="small"
                  style={{ margin: "0 3px", cursor: "pointer", color: themecolors?.textColor }}
                  onClick={() => onEdit(command)} />
              </Tooltip>
              <Tooltip title={translate("cancelSchedule")}>
                <CancelIcon className="actionButton" fontSize="small"
                  style={{ margin: "0 3px", cursor: "pointer", color: isCancelling ? "#ccc" : themecolors?.menuActiveBackground }}
                  onClick={() => !isCancelling && onCancel(command)} />
              </Tooltip>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Section wrapper ────────────────────────────────────────────────────────
function CommandSection({ title, children, count, emptyText, themecolors }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <strong style={{ fontSize: 12, fontWeight: 600 }}>{title}</strong>
        <span style={{ fontSize: 11, padding: "1px 7px", borderRadius: 10, background: `${themecolors.themeLightColor}44` }}>
          {count}
        </span>
      </div>
      {count === 0 ? (
        <div style={{ fontSize: 12, color: themecolors.textColor, opacity: 0.6, padding: "4px 0" }}>{emptyText}</div>
      ) : children}
    </div>
  );
}

// ── Tab bar ────────────────────────────────────────────────────────────────
function TabBar({ active, onTabChange, translate, themecolors }) {
  const tabs = [
    { key: "assigned", label: translate("commandsAssignedTab"), icon: <LinkIcon style={{ fontSize: 14 }} /> },
    { key: "schedule", label: translate("commandsScheduleTab"), icon: <CalendarTodayIcon style={{ fontSize: 14 }} /> },
  ];
  return (
    <div style={{ display: "flex", borderBottom: `2px solid ${themecolors.themeLightColor}44`, marginBottom: 12 }}>
      {tabs.map((tab) => (
        <button key={tab.key} onClick={() => onTabChange(tab.key)}
          style={{
            display: "flex", alignItems: "center", gap: 4, padding: "6px 14px",
            background: "transparent", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 500,
            color: active === tab.key ? themecolors.menuActiveBackground : themecolors.textColor,
            borderBottom: active === tab.key ? `2px solid ${themecolors.menuActiveBackground}` : "2px solid transparent",
            marginBottom: -2, transition: "all 0.15s",
          }}>
          {tab.icon}
          {tab.label}
        </button>
      ))}
    </div>
  );
}

// ── Main SendCommand ────────────────────────────────────────────────────────
function SendCommand({ id, translate, themecolors, dispatch, descriptionList, allCommands, logInUser, closeModel }) {
  // ── State ──
  const [activeTab, setActiveTab] = useState("assigned");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [createData, setCreateData] = useState({ ...emptyCreateData });
  const [isCreateVisible, setIsCreateVisible] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);
  const [unassigningId, setUnassigningId] = useState(null);
  const [assigningId, setAssigningId] = useState(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleEditingItem, setScheduleEditingItem] = useState(null);
  const [scheduleEditingCommand, setScheduleEditingCommand] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({ ...emptyScheduleForm });
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [cancellingSchedule, setCancellingSchedule] = useState(null);
  const [cancelScheduleOpen, setCancelScheduleOpen] = useState(false);
  const [scheduledCommands, setScheduledCommands] = useState([]);
  const [scheduledLoading, setScheduledLoading] = useState(false);
  const [availableTypes, setAvailableTypes] = useState(null);
  const [assignedDescriptionList, setAssignedDescriptionList] = useState(null);
  const [search, setSearch] = useState("");

  const t = useCallback((key) => translate(key), [translate]);

  // ── Effects ──
  useEffect(() => {
    fetchCommandTypes();
    fetchScheduledCommands();
    fetchAllCommandsIfNeeded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── API methods ──
  const fetchAllCommandsIfNeeded = () => {
    if (allCommands && allCommands.length > 0) return;
    const userId = logInUser && logInUser.id;
    if (!userId) return;
    instance({
      method: "GET",
      url: `/api/commands/get?userId=${userId}&limit=-1&all=true`,
    })
      .then((response) => {
        const raw = response?.data;
        const data = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : null);
        if (data) {
          dispatch(getCommands(data));
        }
      })
      .catch(() => {});
  };

  const fetchCommandTypes = () => {
    if (!id) return;
    instance({
      url: "/api/commands/types",
      method: "GET",
      params: { deviceId: id, textChannel: false },
    })
      .then((res) => {
        const types =
          Array.isArray(res)
            ? res
            : Array.isArray(res?.data)
              ? res.data
              : Array.isArray(res?.data?.data)
                ? res.data.data
                : null;
        if (types && types.length > 0) {
          const minimal = new Set(SYSTEM_COMMAND_TYPES);
          const filtered = types
            .map((tp) => (typeof tp === "string" ? tp : tp?.type))
            .filter((tp) => tp && minimal.has(tp));
          setAvailableTypes(filtered.length ? filtered : null);
        }
      })
      .catch(() => {});
  };

  const fetchScheduledCommands = () => {
    if (!id) return;
    setScheduledLoading(true);
    instance({ url: "/api/scheduledCommands", method: "GET", params: { deviceId: id } })
      .then((res) => {
        let list = [];
        if (Array.isArray(res)) list = res;
        else if (res && Array.isArray(res.data)) list = res.data;
        else if (res && res.data && Array.isArray(res.data.data)) list = res.data.data;
        setScheduledCommands(list.filter((c) => (c.status || "PENDING") === "PENDING"));
        setScheduledLoading(false);
      })
      .catch(() => setScheduledLoading(false));
  };

  const refreshAssignedList = () => {
    if (id == null) return;
    instance({
      url: "/api/commands/send",
      method: "GET",
      params: { deviceId: id },
    })
      .then((res) => {
        const commands =
          res?.data?.data ?? res?.data ?? (Array.isArray(res) ? res : []);
        setAssignedDescriptionList(Array.isArray(commands) ? commands : []);
      })
      .catch(() => {});
  };

  // ── Item list builders ──
  const getCommandDescription = (cmd) => {
    if (cmd.type === "custom" && cmd.attributes && cmd.attributes.data) {
      return cmd.attributes.data;
    }
    const descKey = `commands.desc.${cmd.type}`;
    const translated = t(descKey);
    return translated !== descKey ? translated : "";
  };

  const getSystemItems = () => {
    return SYSTEM_COMMAND_TYPES.map((type) => {
      const descKey = `commands.desc.${type}`;
      const descTranslated = t(descKey);
      const description = descTranslated !== descKey ? descTranslated : "";
      return {
        key: `system:${type}`,
        kind: "system",
        type,
        name: t(`commands.${type}`) || type,
        description,
      };
    });
  };

  const getAssignedCustomItems = () => {
    const list = assignedDescriptionList || descriptionList || [];
    return list.map((cmd) => ({
      key: `custom:${cmd.id}`,
      kind: "custom",
      type: cmd.type,
      name: cmd.description || t(`commands.${cmd.type}`) || cmd.type,
      description: getCommandDescription(cmd),
      id: cmd.id,
      attributes: cmd.attributes || {},
      raw: cmd,
    }));
  };

  const getUnassignedItems = () => {
    const assignedSource = assignedDescriptionList || descriptionList || [];
    const assignedIds = new Set((assignedSource || []).map((c) => c.id));
    return (allCommands || [])
      .filter((cmd) => !assignedIds.has(cmd.id))
      .map((cmd) => ({
        key: `pool:${cmd.id}`,
        kind: "custom",
        type: cmd.type,
        name: cmd.description || t(`commands.${cmd.type}`) || cmd.type,
        description: getCommandDescription(cmd),
        id: cmd.id,
        attributes: cmd.attributes || {},
        raw: cmd,
      }));
  };

  const filterItems = (items) => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (item) => item.name.toLowerCase().includes(q) || item.type.toLowerCase().includes(q) || item.description.toLowerCase().includes(q)
    );
  };

  // ── Actions ──
  const handleSend = async (item, attributes) => {
    const payload = {
      id: item.kind === "custom" ? item.id : 0,
      deviceId: parseInt(id),
      type: item.type,
      description: item.name,
      attributes,
    };
    try {
      const response = await axios({ url: "/api/commands/send", method: "POST", data: payload });
      if (response.data && response.data.statusCode === "200") {
        if (closeModel) closeModel();
        dispatch(notifySuccess(t("commandSent")));
      } else if (response.data && response.data.statusCode === "202") {
        dispatch(notifySuccess(t("commandQueued")));
      } else {
        dispatch(notifyError(t("error")));
      }
    } catch (error) {
      errorHandler(error, dispatch);
    }
  };

  const handleAssign = async (item) => {
    setAssigningId(item.id);
    try {
      await instance({
        url: "/api/permissions", method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        data: { deviceId: parseInt(id), commandId: item.id },
      });
      dispatch(notifySuccess(t("savedCommandsAssignedSuccessfully")));
      refreshAssignedList();
    } catch (error) {
      errorHandler(error, dispatch);
    } finally {
      setAssigningId(null);
    }
  };

  const handleUnassign = async (item) => {
    setUnassigningId(item.id);
    try {
      await instance({
        url: "/api/permissions", method: "DELETE",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        data: { deviceId: parseInt(id), commandId: item.id },
      });
      dispatch(notifySuccess(t("savedCommandsUnAssignedSuccessfully")));
      refreshAssignedList();
    } catch (error) {
      errorHandler(error, dispatch);
    } finally {
      setUnassigningId(null);
    }
  };

  const openCreate = () => {
    setShowCreateForm(true);
    setEditingItem(null);
    setCreateData({ ...emptyCreateData });
    setIsCreateVisible(false);
  };

  const openEdit = (item) => {
    setShowCreateForm(true);
    setEditingItem(item);
    setCreateData({ ...item.raw, attributes: { ...item.attributes } });
    setIsCreateVisible(true);
  };

  const openDelete = (item) => setDeletingItem(item);

  const closeForm = () => {
    setShowCreateForm(false);
    setEditingItem(null);
  };

  const handleCreateChange = (field, value) => {
    const attrFields = new Set(["data","frequency","frequencyType","timezone","phoneNumber","messages","index","server","port","radius","enable"]);
    if (attrFields.has(field)) {
      setCreateData((prev) => {
        const next = { ...prev, attributes: { ...prev.attributes, [field]: value } };
        setIsCreateVisible(Boolean(next.description));
        return next;
      });
    } else {
      setCreateData((prev) => {
        const next = { ...prev, [field]: value };
        setIsCreateVisible(Boolean(next.description));
        return next;
      });
    }
  };

  const submitCreate = async () => {
    const isEdit = Boolean(editingItem);
    const textChannel = createData.textChannel === "Yes" ? true : createData.textChannel === "No" ? false : Boolean(createData.textChannel);
    const payload = { ...createData, deviceId: 0, textChannel };

    try {
      const res = await instance({
        url: isEdit ? `/api/commands/${createData.id}` : "/api/commands",
        method: isEdit ? "PUT" : "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        data: payload,
      });
      dispatch(addCommands(res));
      if (!isEdit) {
        await instance({
          url: "/api/permissions", method: "POST",
          headers: { Accept: "application/json", "Content-Type": "application/json" },
          data: { deviceId: parseInt(id), commandId: res.id },
        });
        dispatch(notifySuccess(t("commandCreated")));
        refreshAssignedList();
      } else {
        dispatch(notifySuccess(t("commandUpdated")));
      }
      setShowCreateForm(false);
      setEditingItem(null);
    } catch (error) {
      errorHandler(error, dispatch);
    }
  };

  const submitDelete = async () => {
    try {
      await instance({
        url: `/api/commands/${deletingItem.id}`, method: "DELETE",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
      });
      dispatch(removedCommands(deletingItem.raw || deletingItem));
      dispatch(notifySuccess(t("commandDeleted")));
      setDeletingItem(null);
    } catch (error) {
      errorHandler(error, dispatch);
    }
  };

  // ── Schedule actions ──
  const openScheduleCreate = (item) => {
    const nextHour = moment().add(1, "hour").startOf("hour").toISOString();
    const attrs = item.kind === "custom" ? (item.attributes || {}) : {};
    setScheduleOpen(true);
    setScheduleEditingItem(item);
    setScheduleEditingCommand(null);
    setScheduleForm({
      scheduledTime: nextHour,
      description: item.name || "",
      data: attrs.data || "", frequency: attrs.frequency || "", frequencyType: attrs.frequencyType || "s",
      timezone: attrs.timezone || "", phoneNumber: attrs.phoneNumber || "", messages: attrs.messages || "",
      index: attrs.index || "", server: attrs.server || "", port: attrs.port || "",
      radius: attrs.radius || "", enable: Boolean(attrs.enable),
    });
  };

  const openScheduleEdit = (command) => {
    const attrs = command.attributes || {};
    setScheduleOpen(true);
    setScheduleEditingCommand(command);
    setScheduleEditingItem(null);
    setScheduleForm({
      scheduledTime: command.scheduledTime || moment().add(1, "hour").startOf("hour").toISOString(),
      description: command.description || "",
      data: attrs.data || "", frequency: attrs.frequency || "", frequencyType: attrs.frequencyType || "s",
      timezone: attrs.timezone || "", phoneNumber: attrs.phoneNumber || "", messages: attrs.messages || "",
      index: attrs.index || "", server: attrs.server || "", port: attrs.port || "",
      radius: attrs.radius || "", enable: Boolean(attrs.enable),
    });
  };

  const closeSchedule = () => {
    setScheduleOpen(false);
    setScheduleEditingItem(null);
    setScheduleEditingCommand(null);
  };

  const handleScheduleFormChange = (field, value) => {
    setScheduleForm((prev) => ({ ...prev, [field]: value }));
  };

  const submitSchedule = async () => {
    const isEdit = Boolean(scheduleEditingCommand);
    const type = isEdit ? scheduleEditingCommand.type : scheduleEditingItem.type;
    const attributes = buildCommandAttributes({ ...scheduleForm, type });
    const payload = {
      deviceId: parseInt(id),
      type,
      description: scheduleForm.description,
      textChannel: false,
      scheduledTime: scheduleForm.scheduledTime,
      attributes,
    };

    setScheduleSaving(true);
    try {
      if (isEdit) {
        await instance({
          url: `/api/scheduledCommands/${scheduleEditingCommand.id}`, method: "PUT",
          headers: { Accept: "application/json", "Content-Type": "application/json" },
          data: { ...payload, id: scheduleEditingCommand.id },
        });
        dispatch(notifySuccess(t("scheduleUpdated")));
      } else {
        await instance({
          url: "/api/scheduledCommands", method: "POST",
          headers: { Accept: "application/json", "Content-Type": "application/json" },
          data: payload,
        });
        dispatch(notifySuccess(t("scheduleCreated")));
      }
      closeSchedule();
      fetchScheduledCommands();
    } catch (error) {
      errorHandler(error, dispatch);
    } finally {
      setScheduleSaving(false);
    }
  };

  const openCancelSchedule = (command) => {
    setCancelScheduleOpen(true);
    setCancellingSchedule(command);
  };

  const submitCancelSchedule = async () => {
    try {
      await instance({
        url: `/api/scheduledCommands/${cancellingSchedule.id}/cancel`, method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
      });
      dispatch(notifySuccess(t("scheduleCancelled")));
      setCancelScheduleOpen(false);
      setCancellingSchedule(null);
      fetchScheduledCommands();
    } catch (error) {
      errorHandler(error, dispatch);
    }
  };

  // ── Render helpers ──
  const renderCreateForm = () => {
    let typeOptions = availableTypes || SYSTEM_COMMAND_TYPES;
    const currentType = createData?.type;
    if (currentType && Array.isArray(typeOptions) && !typeOptions.includes(currentType) && availableTypes) {
      typeOptions = [...typeOptions, currentType];
    }

    return (
      <div style={{ padding: 16 }}>
        <strong style={{ display: "block", marginBottom: 10, fontSize: 13 }}>
          {editingItem ? translate("sharedUpdate") : translate("sharedCreate")} {translate("commandSend")}
        </strong>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <TextField margin="dense" label={translate("sharedDescription")} variant="outlined" fullWidth
              value={createData.description || ""}
              onChange={(e) => handleCreateChange("description", e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <TextField select margin="dense" label={translate("sharedType")} variant="outlined" fullWidth
              value={createData.type || "custom"}
              onChange={(e) => handleCreateChange("type", e.target.value)}>
              {typeOptions.map((tp) => (
                <MenuItem key={tp} value={tp}>{translate(`commands.${tp}`) || tp}</MenuItem>
              ))}
            </TextField>
          </Grid>
          {TYPES_WITH_DATA.has(createData.type) && (
            <Grid item xs={12}>
              <TextField margin="dense" label={translate("commandData")} variant="outlined" fullWidth
                value={(createData.attributes && createData.attributes.data) || ""}
                onChange={(e) => handleCreateChange("data", e.target.value)} />
            </Grid>
          )}
          {createData.type === "positionPeriodic" && (
            <>
              <Grid item xs={6}>
                <TextField type="number" margin="dense" label={translate("commandFrequency")} variant="outlined" fullWidth
                  value={(createData.attributes && createData.attributes.frequency) || ""}
                  onChange={(e) => handleCreateChange("frequency", e.target.value)} />
              </Grid>
              <Grid item xs={6}>
                <TextField select margin="dense" label={translate("commandFrequencyType")} variant="outlined" fullWidth
                  value={(createData.attributes && createData.attributes.frequencyType) || "s"}
                  onChange={(e) => handleCreateChange("frequencyType", e.target.value)}>
                  {FREQUENCY_TYPES.map((ft) => <MenuItem key={ft.type} value={ft.type}>{ft.type}</MenuItem>)}
                </TextField>
              </Grid>
            </>
          )}
          {createData.type === "setTimezone" && (
            <Grid item xs={12}>
              <TextField select margin="dense" label={translate("commandTimezone")} variant="outlined" fullWidth
                value={(createData.attributes && createData.attributes.timezone) || ""}
                onChange={(e) => handleCreateChange("timezone", e.target.value)}>
                {timeZonesTypes.map((tz) => <MenuItem key={tz.key} value={tz.key}>{tz.label}</MenuItem>)}
              </TextField>
            </Grid>
          )}
          {TYPES_WITH_INDEX.has(createData.type) && (
            <Grid item xs={6}>
              <TextField type="number" margin="dense" label={translate("commandIndex")} variant="outlined" fullWidth
                value={(createData.attributes && createData.attributes.index) || ""}
                onChange={(e) => handleCreateChange("index", e.target.value)} />
            </Grid>
          )}
          {TYPES_WITH_PHONE.has(createData.type) && (
            <Grid item xs={6}>
              <TextField margin="dense" label={translate("commandPhone")} variant="outlined" fullWidth
                value={(createData.attributes && createData.attributes.phoneNumber) || ""}
                onChange={(e) => handleCreateChange("phoneNumber", e.target.value)} />
            </Grid>
          )}
          {TYPES_WITH_MESSAGE.has(createData.type) && (
            <Grid item xs={12}>
              <TextField margin="dense" label={translate("Messages")} variant="outlined" fullWidth
                value={(createData.attributes && createData.attributes.messages) || ""}
                onChange={(e) => handleCreateChange("messages", e.target.value)} />
            </Grid>
          )}
          {createData.type === "setConnection" && (
            <>
              <Grid item xs={6}>
                <TextField margin="dense" label={translate("commandServer")} variant="outlined" fullWidth
                  value={(createData.attributes && createData.attributes.server) || ""}
                  onChange={(e) => handleCreateChange("server", e.target.value)} />
              </Grid>
              <Grid item xs={6}>
                <TextField type="number" margin="dense" label={translate("commandPort")} variant="outlined" fullWidth
                  value={(createData.attributes && createData.attributes.port) || ""}
                  onChange={(e) => handleCreateChange("port", e.target.value)} />
              </Grid>
            </>
          )}
          {createData.type === "novementAlarm" && (
            <Grid item xs={12}>
              <TextField type="number" margin="dense" label={translate("commandRadius")} variant="outlined" fullWidth
                value={(createData.attributes && createData.attributes.radius) || ""}
                onChange={(e) => handleCreateChange("radius", e.target.value)} />
            </Grid>
          )}
          {TYPES_WITH_ENABLE.has(createData.type) && (
            <Grid item xs={12} style={{ display: "flex", alignItems: "center", paddingLeft: 4 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13 }}>
                <input type="checkbox"
                  checked={(createData.attributes && createData.attributes.enable) || false}
                  onChange={(e) => handleCreateChange("enable", e.target.checked)} />
                {translate("commandEnable")}
              </label>
            </Grid>
          )}
        </Grid>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
          <Button size="small" className="button-white" onClick={closeForm}>
            {translate("sharedCancel")}
          </Button>
          <Button size="small" disabled={!isCreateVisible} onClick={submitCreate}>
            {editingItem ? translate("sharedUpdate") : translate("sharedCreate")}
          </Button>
        </div>
      </div>
    );
  };

  const renderAssignedTab = (systemItems, assignedCustomItems, unassignedItems, canSend, canCreate, canUpdate, canDelete, canAssign, canUnassign) => {
    const availableItems = [...systemItems, ...assignedCustomItems];

    return (
      <>
        <CommandSection title={translate("availableCommands")} count={availableItems.length}
          emptyText={search ? translate("noCommandsMatchSearch") : translate("noCommandsAvailable")}
          themecolors={themecolors}>
          {availableItems.map((item) => (
            <CommandRow key={item.key} item={item} mode="available"
              canSend={canSend} canUnassign={item.kind === "custom" && canUnassign}
              onSend={handleSend} onUnassign={handleUnassign}
              isUnassigning={unassigningId === item.id} translate={translate} themecolors={themecolors} />
          ))}
        </CommandSection>

        <CommandSection title={translate("customCommandsPool")} count={unassignedItems.length}
          emptyText={search ? translate("noCommandsMatchSearch") : (allCommands && allCommands.length === 0 ? translate("noCustomCommands") : translate("allCustomCommandsAssigned"))}
          themecolors={themecolors}>
          {unassignedItems.map((item) => (
            <CommandRow key={item.key} item={item} mode="assignable"
              canAssign={canAssign} canEdit={canUpdate} canDelete={canDelete}
              onAssign={handleAssign} onEdit={openEdit} onDelete={openDelete}
              isAssigning={assigningId === item.id} translate={translate} themecolors={themecolors} />
          ))}
        </CommandSection>
      </>
    );
  };

  const renderScheduleTab = (availableItems) => {
    const canUpdate = checkPrivileges("commandUpdate");
    const canSchedule = checkPrivileges("commandSend");
    const q = (search || "").trim().toLowerCase();

    const filteredScheduled = scheduledCommands.filter((c) => {
      if (!q) return true;
      const name = (c.description || "").toLowerCase();
      const type = (c.type || "").toLowerCase();
      return name.includes(q) || type.includes(q);
    }).sort((a, b) => new Date(a.scheduledTime) - new Date(b.scheduledTime));

    const filteredAvailable = availableItems.filter((item) => {
      if (!q) return true;
      return item.name.toLowerCase().includes(q) || item.type.toLowerCase().includes(q);
    });

    if (scheduledLoading) {
      return <div style={{ padding: "20px 0", textAlign: "center", fontSize: 12, opacity: 0.6 }}>{translate("loading")}</div>;
    }

    return (
      <>
        <CommandSection title={translate("scheduledForThisTracker")} count={filteredScheduled.length}
          emptyText={q ? translate("noCommandsMatchSearch") : translate("noScheduledCommandsTracker")}
          themecolors={themecolors}>
          {filteredScheduled.map((cmd) => (
            <ScheduledRow key={cmd.id} command={cmd} canUpdate={canUpdate}
              isCancelling={false} onEdit={openScheduleEdit}
              onCancel={openCancelSchedule} translate={translate} themecolors={themecolors} />
          ))}
        </CommandSection>

        {canSchedule && (
          <CommandSection title={translate("availableToSchedule")} count={filteredAvailable.length}
            emptyText={q ? translate("noCommandsMatchSearch") : translate("commandNotAvailable")}
            themecolors={themecolors}>
            {filteredAvailable.map((item) => {
              const isSystem = item.kind === "system";
              const accentColor = themecolors.menuActiveBackground;
              return (
                <div key={item.key} style={{
                  background: `${themecolors.themeLightColor}22`, borderRadius: 4, marginBottom: 6,
                  border: `1px solid ${themecolors.themeLightColor}44`,
                  display: "flex", alignItems: "center", padding: "6px 10px", gap: 8,
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 13, fontWeight: 500, color: themecolors?.textColor, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {item.name}
                      </span>
                      <span style={{
                        fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em",
                        padding: "1px 6px", borderRadius: 10, flexShrink: 0,
                        background: isSystem ? "transparent" : accentColor,
                        color: isSystem ? themecolors.textColor : "#fff",
                        border: isSystem ? `1px solid ${themecolors.themeLightColor}` : "none",
                      }}>
                        {isSystem ? translate("systemCommand") : translate("customCommand")}
                      </span>
                    </div>
                    {item.description ? (
                      <div style={{ fontSize: 11, color: themecolors?.textColor, opacity: 0.65, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {item.description}
                      </div>
                    ) : null}
                  </div>
                  <Button size="small" onClick={() => openScheduleCreate(item)}
                    style={{ fontSize: 11, padding: "2px 8px", height: 26, flexShrink: 0 }}>
                    <CalendarTodayIcon style={{ fontSize: 13, marginRight: 2 }} />
                    {translate("schedule")}
                  </Button>
                </div>
              );
            })}
          </CommandSection>
        )}
      </>
    );
  };

  // ── Main render ──
  const systemItems = filterItems(getSystemItems());
  const assignedCustomItems = filterItems(getAssignedCustomItems());
  const unassignedItems = filterItems(getUnassignedItems());
  const canSend = checkPrivileges("commandSend");
  const canCreate = checkPrivileges("commandCreate");
  const canUpdate = checkPrivileges("commandUpdate");
  const canDelete = checkPrivileges("commandDelete");
  const canAssign = checkPrivileges("deviceLinkCommand");
  const canUnassign = checkPrivileges("deviceUnlinkCommand");

  const scheduleType = scheduleEditingCommand
    ? scheduleEditingCommand.type
    : scheduleEditingItem
      ? scheduleEditingItem.type
      : "custom";
  const scheduleTitle = scheduleEditingCommand
    ? translate("editScheduledCommand")
    : translate("scheduleCommand");

  if (showCreateForm) {
    return <>{renderCreateForm()}</>;
  }

  return (
    <>
      <div style={{ padding: "8px 16px 0" }}>
        {/* Header */}
        <div style={{ textAlign: "right", padding: "2px 0 6px 0", display: "flex", alignItems: "center" }}
          className="clearfix">
          <h4 className="v-tab-heading pull-left with-border" style={{ marginTop: 8, marginBottom: 4 }}>
            {translate("sharedSavedCommand")}
          </h4>
          <div style={{ margin: "auto" }} />
          <div style={{ width: 200 }}>
            <SearchField
              fullWidth
              label={translate("sharedSearch")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {canCreate && (
            <div style={{ marginTop: 8, marginBottom: 4, marginLeft: 8 }}>
              <Button size="small" onClick={openCreate}>
                + {translate("sharedCreate")}
              </Button>
            </div>
          )}
        </div>
        <TabBar active={activeTab} onTabChange={(tab) => { setActiveTab(tab); setSearch(""); }}
          translate={translate} themecolors={themecolors} />
      </div>
      <div style={{ padding: "0 16px 16px" }}>
        {activeTab === "assigned" &&
          renderAssignedTab(systemItems, assignedCustomItems, unassignedItems, canSend, canCreate, canUpdate, canDelete, canAssign, canUnassign)}
        {activeTab === "schedule" &&
          renderScheduleTab([...systemItems, ...assignedCustomItems])}
      </div>

      {/* Delete confirm */}
      {deletingItem && (
        <ConfirmDialog
          title={translate("areYouWantToDelete")}
          onCancel={() => setDeletingItem(null)}
          children={deletingItem.name}
          onOk={submitDelete}
        />
      )}

      {/* Cancel schedule confirm */}
      {cancelScheduleOpen && (
        <ConfirmDialog
          title={translate("areYouSureToCancelSchedule")}
          onCancel={() => { setCancelScheduleOpen(false); setCancellingSchedule(null); }}
          children={cancellingSchedule && (cancellingSchedule.description || translate(`commands.${cancellingSchedule.type}`))}
          onOk={submitCancelSchedule}
        />
      )}

      {/* Schedule create/edit dialog */}
      {scheduleOpen && (
        <Dialog
          open
          title={scheduleTitle}
          themeColors={themecolors}
          disableFooter
          onClose={closeSchedule}
          dialogHeight={200}
          draggable
          bodyPadding={0}
          maxWidth="xs"
        >
          <div style={{ padding: "4px 20px 16px" }}>
            <ScheduleFormFields
              form={{ ...scheduleForm, type: scheduleType }}
              onChange={handleScheduleFormChange}
              translate={translate}
              themecolors={themecolors}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
              <Button size="small" className="button-white" onClick={closeSchedule} disabled={scheduleSaving}>
                {translate("sharedCancel")}
              </Button>
              <Button size="small" onClick={submitSchedule} disabled={scheduleSaving || !scheduleForm.scheduledTime}>
                {scheduleSaving ? translate("loading") : (scheduleEditingCommand ? translate("sharedUpdate") : translate("schedule"))}
              </Button>
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
}

const mapStateToProps = (state) => ({
  allCommands: state.commands,
  logInUser: state.logInUsers,
});
const ConnectedSendCommand = connect(mapStateToProps)(withTranslationWrapper(SendCommand));

export { ConnectedSendCommand as SendCommand };
export default ConnectedSendCommand;
